import { useState } from 'react';
import { motion } from 'framer-motion';
import { fadeUp } from '@/animations/variants';
import Button from '@/components/common/Button';
import emailjs from '@emailjs/browser';

interface FormData {
  name: string;
  email: string;
  company: string;
  requirement: string;
  budget?: string;
  timeline?: string;
}

export default function ProjectForm() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    requirement: '',
    budget: '',
    timeline: '',
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string>('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      // EmailJS configuration - Initialize EmailJS with public key
      emailjs.init('66w6XR1E104wMeNAH');
      
      const serviceId = 'service_wx5sdgl';
      const templateId = 'template_vb07dtf';
      
      // Prepare template parameters matching your EmailJS template exactly
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        email: formData.email, // For Reply To field
        company: formData.company,
        budget: formData.budget || 'Not specified',
        timeline: formData.timeline || 'Not specified',
        requirement: formData.requirement,
      };
      
      // Send email using EmailJS
      const response = await emailjs.send(serviceId, templateId, templateParams);
      
      console.log('Email sent successfully:', response);
      setIsSubmitted(true);
    } catch (err: any) {
      console.error('EmailJS Error:', err);
      console.error('Error details:', err.text || err.message);
      setError('Failed to send message. Please try again or contact us via WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <motion.div
        className="bg-surface border border-border rounded-xl p-8 text-center"
        variants={fadeUp}
        initial="hidden"
        animate="visible"
      >
        <div className="text-4xl mb-4">✅</div>
        <h3 className="text-2xl font-heading font-semibold text-textPrimary mb-4">
          Thank You!
        </h3>
        <p className="text-textSecondary mb-6">
          We've received your project requirements. Our team will get back to you within 24 hours.
        </p>
        <Button 
          variant="outline" 
          onClick={() => {
            setIsSubmitted(false);
            setFormData({
              name: '',
              email: '',
              company: '',
              requirement: '',
              budget: '',
              timeline: '',
            });
          }}
        >
          Submit Another Project
        </Button>
      </motion.div>
    );
  }

  return (
    <motion.form
      className="bg-surface border border-border rounded-xl p-8 max-w-2xl mx-auto"
      variants={fadeUp}
      initial="hidden"
      animate="visible"
      onSubmit={handleSubmit}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-textPrimary mb-2">
            Full Name *
          </label>
          <input
            type="text"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            className="w-full px-4 py-3 bg-background border border-border rounded-lg text-textPrimary placeholder-textSecondary focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
            placeholder="Your full name"
          />
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-textPrimary mb-2">
            Email Address *
          </label>
          <input
            type="email"
            name="email"
            required
            value={formData.email}
            onChange={handleChange}
            className="w-full px-4 py-3 bg-background border border-border rounded-lg text-textPrimary placeholder-textSecondary focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
            placeholder="your.email@company.com"
          />
        </div>

        {/* Company */}
        <div>
          <label className="block text-sm font-medium text-textPrimary mb-2">
            Company Name *
          </label>
          <input
            type="text"
            name="company"
            required
            value={formData.company}
            onChange={handleChange}
            className="w-full px-4 py-3 bg-background border border-border rounded-lg text-textPrimary placeholder-textSecondary focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
            placeholder="Your company name"
          />
        </div>

        {/* Budget */}
        <div>
          <label className="block text-sm font-medium text-textPrimary mb-2">
            Budget Range
          </label>
          <select
            name="budget"
            value={formData.budget}
            onChange={handleChange}
            className="w-full px-4 py-3 bg-background border border-border rounded-lg text-textPrimary focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
          >
            <option value="">Select budget range</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="na">No worries on Budget</option>
          </select>
        </div>

        {/* Timeline */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-textPrimary mb-2">
            Project Timeline
          </label>
          <select
            name="timeline"
            value={formData.timeline}
            onChange={handleChange}
            className="w-full px-4 py-3 bg-background border border-border rounded-lg text-textPrimary focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
          >
            <option value="">Select timeline</option>
            <option value="asap">ASAP</option>
            <option value="lowtime">1 month</option>
            <option value="mediumtime">2-5 months</option>
            <option value="longtime">6+ months</option>
          </select>
        </div>

        {/* Requirements */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-textPrimary mb-2">
            Project Requirements *
          </label>
          <textarea
            name="requirement"
            required
            rows={6}
            value={formData.requirement}
            onChange={handleChange}
            className="w-full px-4 py-3 bg-background border border-border rounded-lg text-textPrimary placeholder-textSecondary focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors resize-none"
            placeholder="Tell us about your project requirements, goals, and any specific technologies you'd like us to use..."
          />
        </div>
      </div>

      <div className="mt-8">
        {error && (
          <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-sm">
            {error}
          </div>
        )}
        
        <Button 
          type="submit" 
          isLoading={isSubmitting}
          fullWidth
        >
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </Button>
      </div>

      <p className="text-xs text-textSecondary mt-4 text-center">
        We typically respond within 24 hours. 
        For urgent projects, please ping on WhatsApp number - +91 92701 61536.
      </p>
    </motion.form>
  );
}